import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import OilyticsClient from 'api/OilyticsClient';
import map from 'lodash/map';
import { toast } from 'sonner';

export enum DashboardType {
  POWERBI = 'powerbi',
  IFRAME = 'iframe',
  CUSTOM = 'custom',
}

export type Dashboard = {
  id: number;
  isSelected: boolean;
  is_favorite: boolean;
  type: string;
  iframe_src: string;
  name: string;
  tags: { id: string; name: string }[];
  embed_info: {
    report: { id: string; embedUrl: string };
    token: { token: string; expiration?: string };
  };
  report_id: string;
  section?: string;
  supports_user_views: boolean;
};

export interface DashboardTag {
  id: number;
  name: string;
  is_primary?: boolean;
}

export const dashboardKeys = {
  byTag: (tagId: number) => ['dashboards', 'by-tag', tagId] as const,
  detail: (id: number) => ['dashboards', 'detail', id] as const,
  legacyList: (isUserDashboard: boolean) =>
    ['dashboards', 'legacy-list', isUserDashboard] as const,
  tags: ['dashboard-tags'] as const,
};

export function useDashboardTags () {
  return useQuery({
    queryKey: dashboardKeys.tags,
    queryFn: async () => {
      const client = await OilyticsClient.create();
      const { data } = await client.getDashboardTags();
      return data.results as DashboardTag[];
    },
  });
}

// GET /api/dashboards?tags[]=<tagId> — tag 0 means all dashboards.
export function useDashboards (tagId = 0) {
  return useQuery({
    queryKey: dashboardKeys.byTag(tagId),
    queryFn: async () => {
      const client = await OilyticsClient.create();
      const { data } = await client.getDashboards(tagId);
      return data.results as Dashboard[];
    },
  });
}

// PowerBI embed tokens expire (~1h); once expired, every visual dies with a
// TokenExpired error until the page is reloaded. Each getDashboard call mints
// a fresh token, so refetching before expiration keeps embeds alive.
const TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000;

export function useDashboard (id?: number) {
  return useQuery({
    queryKey: dashboardKeys.detail(id as number),
    queryFn: async () => {
      const client = await OilyticsClient.create();
      const { data } = await client.getDashboard(id as number);
      return data as Dashboard;
    },
    enabled: !!id,
    // refetchInterval as a function (react-query v5) is re-evaluated with the
    // latest data after every fetch, so each refresh schedules the next one
    // from the new token's expiration. Tokens live well past the 5-minute
    // margin, so the delay is always positive; the 10s floor guards against
    // a tight refetch loop if the API ever returns an already-stale token.
    refetchInterval: (query) => {
      const expiration = query.state.data?.embed_info?.token?.expiration;
      if (!expiration) return false;
      return Math.max(
        new Date(expiration).getTime() - Date.now() - TOKEN_REFRESH_MARGIN_MS,
        10_000
      );
    },
    // react-query pauses intervals for unfocused windows by default; a
    // long-lived background tab is exactly where the token would expire
    refetchIntervalInBackground: true,
  });
}

// GET /api/dashboard/ (trailing slash) — the legacy flat list. Tags are
// joined into a display string, matching the old getDashboardsList selector.
export function useDashboardsList (isUserDashboard = false) {
  return useQuery({
    queryKey: dashboardKeys.legacyList(isUserDashboard),
    queryFn: async () => {
      const client = await OilyticsClient.create();
      const { data } = await client.getDashboardList(isUserDashboard);
      return map(data.results as Dashboard[], (d) => ({
        ...d,
        tags: map(d.tags, 'name').join(', '),
      }));
    },
  });
}

// Flips is_favorite across every cached dashboard list and detail entry so
// the favorites panel, the main list stars, and the hero can never disagree
// (the invariant the old redux setDashboardFavorite reducer enforced).
function setFavoriteInCaches (
  queryClient: ReturnType<typeof useQueryClient>,
  id: number,
  isFavorited: boolean
) {
  queryClient.setQueriesData<Dashboard[]>(
    { queryKey: ['dashboards', 'by-tag'] },
    (old) =>
      old && map(old, (d) => (d.id === id ? { ...d, is_favorite: isFavorited } : d))
  );
  queryClient.setQueryData<Dashboard>(
    dashboardKeys.detail(id),
    (old) => old && { ...old, is_favorite: isFavorited }
  );
}

// Both toggles are optimistic: the star flips immediately and rolls back if
// the API call fails — same UX as the old redux thunks.
export function useFavoriteDashboard () {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const client = await OilyticsClient.create();
      await client.favoriteDashboard(id);
    },
    onMutate: (id) => setFavoriteInCaches(queryClient, id, true),
    onSuccess: () => toast.success('Dashboard added to favorites'),
    onError: (error, id) => {
      console.error(error);
      toast.error('Something went wrong! Dashboard could not be favorited');
      setFavoriteInCaches(queryClient, id, false);
    },
  });
}

export function useUnfavoriteDashboard () {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const client = await OilyticsClient.create();
      await client.unfavoriteDashboard(id);
    },
    onMutate: (id) => setFavoriteInCaches(queryClient, id, false),
    onError: (error, id) => {
      console.error(error);
      toast.error('Something went wrong! Dashboard could not be unfavorited');
      setFavoriteInCaches(queryClient, id, true);
    },
  });
}
